import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_FILTERS, RELATIVE_PRESETS } from '../constants';
import { getDateRangeByType, getTimezone } from '../utils/dateUtils';
import { getSavedFilters, saveFilters } from '../utils/filterUtils';

/**
 * Filters live in the query string so a view can be shared as a link and the
 * back button works. localStorage only seeds the defaults when the URL carries
 * no filters at all.
 *
 * Relative presets are stored by key (`range=last7Days`), not as absolute
 * dates, so a saved or shared range keeps rolling. Absolute `start`/`end` are
 * written only when the user hand-picks dates.
 */

// Read once per page load; we are the only writer.
const savedSeed = getSavedFilters() || {};

const resolveDates = (params) => {
  if (params.has('range')) {
    const range = params.get('range');
    if (RELATIVE_PRESETS.has(range)) {
      return { dateRange: range, ...getDateRangeByType(range, params.get('tz') || undefined) };
    }
  }
  if (params.has('start') && params.has('end')) {
    return { dateRange: '', start: params.get('start'), end: params.get('end') };
  }

  // Fall back to the last-used range.
  const savedRange = savedSeed.dateRange;
  if (RELATIVE_PRESETS.has(savedRange)) {
    return {
      dateRange: savedRange,
      ...getDateRangeByType(savedRange, savedSeed.timezone),
    };
  }
  if (savedSeed.start && savedSeed.end) {
    return { dateRange: '', start: savedSeed.start, end: savedSeed.end };
  }
  return {
    dateRange: DEFAULT_FILTERS.dateRange,
    ...getDateRangeByType(DEFAULT_FILTERS.dateRange, savedSeed.timezone),
  };
};

const readParam = (params, key, savedValue, fallback) => {
  if (params.has(key)) return params.get(key);
  return savedValue ?? fallback;
};

/** Serialise filters into the query string. */
const toSearchParams = (next) => {
  // A preset and explicit dates are mutually exclusive.
  const usesPreset = RELATIVE_PRESETS.has(next.dateRange);
  const resolved = usesPreset ? getDateRangeByType(next.dateRange, next.timezone) : next;

  const params = new URLSearchParams();
  params.set('project', next.project ?? '');
  params.set('interval', next.interval);
  params.set('tz', next.timezone);
  if (usesPreset) {
    params.set('range', next.dateRange);
  } else {
    params.set('start', resolved.start);
    params.set('end', resolved.end);
  }
  if (next.campaignId) params.set('campaign', next.campaignId);
  if (next.excludeNoInstalls) params.set('noEmpty', '1');
  if (next.compare) params.set('compare', '1');
  return params;
};

const toPersisted = (next) => {
  const usesPreset = RELATIVE_PRESETS.has(next.dateRange);
  const resolved = usesPreset ? getDateRangeByType(next.dateRange, next.timezone) : next;
  return {
    project: next.project,
    interval: next.interval,
    timezone: next.timezone,
    excludeNoInstalls: next.excludeNoInstalls,
    dateRange: usesPreset ? next.dateRange : '',
    start: resolved.start,
    end: resolved.end,
  };
};

export const useFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const timezone = readParam(searchParams, 'tz', savedSeed.timezone, getTimezone());
    const { dateRange, start, end } = resolveDates(searchParams);

    return {
      project: readParam(searchParams, 'project', savedSeed.project, DEFAULT_FILTERS.project),
      interval: readParam(searchParams, 'interval', savedSeed.interval, DEFAULT_FILTERS.interval),
      campaignId: searchParams.get('campaign') || DEFAULT_FILTERS.campaignId,
      excludeNoInstalls: searchParams.has('noEmpty')
        ? searchParams.get('noEmpty') === '1'
        : (savedSeed.excludeNoInstalls ?? DEFAULT_FILTERS.excludeNoInstalls),
      compare: searchParams.get('compare') === '1',
      timezone,
      dateRange,
      start,
      end,
    };
  }, [searchParams]);

  const setFilters = useCallback(
    (patch) => {
      const next = { ...filters, ...patch };
      saveFilters(toPersisted(next));
      setSearchParams(toSearchParams(next));
    },
    [filters, setSearchParams],
  );

  return [filters, setFilters];
};

/**
 * Landing URL for the dashboard, carrying the resolved defaults so the address
 * bar is shareable from the very first render instead of only after the user
 * touches a filter.
 */
export const buildDefaultStatsPath = () => {
  const timezone = savedSeed.timezone || getTimezone();
  const params = new URLSearchParams();
  const { dateRange, start, end } = resolveDates(params);

  return `/stats?${toSearchParams({
    project: savedSeed.project ?? DEFAULT_FILTERS.project,
    interval: savedSeed.interval ?? DEFAULT_FILTERS.interval,
    campaignId: DEFAULT_FILTERS.campaignId,
    excludeNoInstalls: savedSeed.excludeNoInstalls ?? DEFAULT_FILTERS.excludeNoInstalls,
    compare: false,
    timezone,
    dateRange,
    start,
    end,
  })}`;
};
